import { graphql, useStaticQuery } from 'gatsby';
import { camelCase } from 'lodash';

import {
  DEFAULT_EXCLUDED_PAGES,
  DEFAULT_TITLE_FIELD,
  DEFAULT_TITLE_OVERRIDE,
} from './constants';
import {
  SiteNode,
  SitePageQueryResult,
  TreeItem,
  UseSiteTreeParams,
} from './models';
import { createTree } from './utils';

const sortMethod = (title1: string | null, title2: string | null): number => {
  if (title1 === null || title1 === '') return 1;
  if (title2 === null || title2 === '') return -1;
  if (title1 < title2) return -1;
  if (title1 > title2) return 1;

  return 0;
};

const useSiteTree = ({
  lang = null,
  defaultLang = null,
  exclude = DEFAULT_EXCLUDED_PAGES,
  titleOverride = DEFAULT_TITLE_OVERRIDE,
  entryTitleField,
}: UseSiteTreeParams): TreeItem[] => {
  const { allSitePage } = useStaticQuery<SitePageQueryResult>(graphql`
    query AllSitePageQuery {
      allSitePage(filter: { context: { enableSearchIndexing: { eq: true } } }) {
        nodes {
          id
          path
          context {
            seoTitle
            shortName
            locale
          }
        }
      }
    }
  `);

  // TODO: remove camelCase when migration automation is ready
  const titleField = entryTitleField
    ? camelCase(entryTitleField)
    : DEFAULT_TITLE_FIELD;

  const nodes: SiteNode[] = [...allSitePage.nodes]
    .sort((page1, page2) =>
      sortMethod(page1.context[titleField], page2.context[titleField])
    )
    .map((item) => {
      if (item.context?.locale === null) {
        return {
          ...item,
          context: {
            ...item.context,
            locale: defaultLang,
          },
        };
      }

      return item;
    })
    .filter((item) => {
      if (lang) {
        return lang.includes(item.context?.locale as string);
      }

      return true;
    })
    .map(({ id, path, context }) => ({
      title: context[titleField],
      id,
      path,
    }));

  return createTree({
    nodes,
    exclude,
    titleOverride,
  });
};

export default useSiteTree;
